import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { useAuth } from '../context/AuthContext';

import { getCollection } from '../firebase/firestore';
import Button from '../components/common/Button';
import Loader from '../components/common/Loader';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalMessages: 0,
    unreadMessages: 0,
    services: 0,
    portfolio: 0
  });

  useEffect(() => {
    // Initialize AOS
    AOS.init({
      duration: 1000,
      once: true,
      easing: 'ease-in-out'
    });
  }, []);

  useEffect(() => {
    if (!user) {
      navigate('/admin/login');
      return;
    }

    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Check if Firestore is available
      const { db } = await import('../firebase/config');

      if (!db) {
        console.warn('Firestore not configured');
        setLoading(false);
        return;
      }

      // Fetch all data
      try {
        // Fetch messages
        const messagesData = await getCollection('messages');

        // Filter today's messages
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const todayMessages = messagesData.filter(msg => {
          if (msg.createdAt && msg.createdAt.toDate) {
            const msgDate = msg.createdAt.toDate();
            msgDate.setHours(0, 0, 0, 0);
            return msgDate.getTime() === today.getTime();
          }
          return false;
        });

        setMessages(todayMessages.slice(0, 5)); // Show latest 5 from today

        // Fetch services count
        let servicesCount = 0;
        try {
          const servicesData = await getCollection('services');
          servicesCount = servicesData.filter(s => s.active !== false).length;
        } catch (err) {
          console.warn('Could not fetch services:', err.message);
        }

        // Fetch portfolio count
        let portfolioCount = 0;
        try {
          const portfolioData = await getCollection('portfolio');
          portfolioCount = portfolioData.length;
        } catch (err) {
          console.warn('Could not fetch portfolio:', err.message);
        }

        // Calculate stats
        setStats({
          totalMessages: messagesData.length,
          unreadMessages: messagesData.filter(m => !m.read).length,
          services: servicesCount,
          portfolio: portfolioCount
        });
      } catch (firestoreError) {
        console.warn('Firestore not ready yet:', firestoreError.message);
        // Set default stats
        setStats({
          totalMessages: 0,
          unreadMessages: 0,
          services: 0,
          portfolio: 0
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };



  if (loading) {
    return <Loader fullScreen />;
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-page-header">
        <h1>Dashboard</h1>
        <p>Welcome back, {user?.email}</p>
      </div>

      <div className="admin-dashboard-content">
        {/* Stats Cards */}
        <div className="stats-grid">
          <div
            className="stat-card"
            data-aos="fade-up"
            data-aos-duration="800"
            data-aos-delay="100"
          >
            <div className="stat-icon">📧</div>
            <div className="stat-info">
              <h3>{stats.totalMessages}</h3>
              <p>Total Messages</p>
            </div>
          </div>

          <div
            className="stat-card highlight"
            data-aos="fade-up"
            data-aos-duration="800"
            data-aos-delay="200"
          >
            <div className="stat-icon">🔔</div>
            <div className="stat-info">
              <h3>{stats.unreadMessages}</h3>
              <p>Unread Messages</p>
            </div>
          </div>

          <div
            className="stat-card"
            data-aos="fade-up"
            data-aos-duration="800"
            data-aos-delay="300"
          >
            <div className="stat-icon">💼</div>
            <div className="stat-info">
              <h3>{stats.services}</h3>
              <p>Services</p>
            </div>
          </div>

          <div
            className="stat-card"
            data-aos="fade-up"
            data-aos-duration="800"
            data-aos-delay="400"
          >
            <div className="stat-icon">📁</div>
            <div className="stat-info">
              <h3>{stats.portfolio}</h3>
              <p>Portfolio Items</p>
            </div>
          </div>
        </div>

        {/* Recent Messages */}
        <div
          className="admin-section"
          data-aos="fade-up"
          data-aos-duration="1000"
          data-aos-delay="500"
        >
          <div className="section-header">
            <h2>Recent Messages</h2>
            <Button variant="ghost" size="small">View All</Button>
          </div>

          {messages.length === 0 ? (
            <div className="empty-state">
              <p>📭 No messages yet.</p>
              <p style={{ fontSize: '0.9rem', marginTop: '1rem' }}>
                Messages from the contact form will appear here.
              </p>
            </div>
          ) : (
            <div className="messages-list">
              {messages.map((message, index) => (
                <div key={message.id || index} className="message-item">
                  <div className="message-header">
                    <h4>{message.name}</h4>
                    <span className="message-date">
                      {message.createdAt?.toDate?.()?.toLocaleDateString() || 'Recent'}
                    </span>
                  </div>
                  <p className="message-subject">{message.subject}</p>
                  <p className="message-preview">{message.message?.substring(0, 100)}...</p>
                  <div className="message-meta">
                    <span>{message.email}</span>
                    {message.phone && <span>{message.phone}</span>}
                    {!message.read && <span className="unread-badge">New</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div
          className="admin-section"
          data-aos="fade-up"
          data-aos-duration="1000"
          data-aos-delay="600"
        >
          <h2>Quick Actions</h2>
          <div className="quick-actions">
            <button className="action-card" onClick={() => navigate('/admin/services')}>
              <span className="action-icon">📝</span>
              <span>Manage Services</span>
            </button>

            <button className="action-card" onClick={() => navigate('/admin/testimonials')}>
              <span className="action-icon">💬</span>
              <span>Manage Testimonials</span>
            </button>
            <button className="action-card" onClick={() => navigate('/admin/partners')}>
              <span className="action-icon">🤝</span>
              <span>Manage Partners</span>
            </button>
            <button className="action-card" onClick={() => navigate('/admin/settings')}>
              <span className="action-icon">⚙️</span>
              <span>Site Settings</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
